import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import isEqual from "lodash/isEqual";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import {
    VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES,
    VerificationServiceDetailsReferenceData
} from "@constants/reference-data";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { ModalTypes } from "@enums/modal-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { MessagingTemplateCache } from "@idw-types/data/messaging-template";
import { WorkflowChangeRequest } from "@idw-types/data/workflow-change-request";
import { sortStrings } from "@utils/string-util";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { VerificationServiceDetailsTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-tab-container";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig, getSelectedMessagingTemplates } from "@utils/messaging-template/messaging-template-util";
import { LanguageSelectorContainer } from "@containers/common/language-selector-container/language-selector-container";
import styles from "./workflow-service-details-modal.less";
import { getUpdatedAxnRpServices } from "@utils/workflow-util";
import { excludeServicesByApiKeys } from "@utils/challenger-policy-util";
import { setPartialCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { LanguageSelectorContainerTypes } from "@enums/language-selector-container-types";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";
import { getApAssertionStatusMaps } from "@utils/data-util";
import { getCommonReferenceDataState, getSessionState } from "@redux/selectors/root-selectors";

type WorkflowServiceDetailsModalProps = {
    initialSelectedService: FormattedRpServiceConfiguration;
    initialRpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    hiddenServiceApiKeys?: Array<string>;
    availableLanguages: Array<LocaleTypes>;
    initialWorkflowMessagingTemplateCache: MessagingTemplateCache;
    initialWorkflowChangeRequests: Array<WorkflowChangeRequest>;
    initialSelectedLanguage: LocaleTypes;
    onModalClose: () => void;
    setRpServiceConfigurationsState: (rpServices: Array<FormattedRpServiceConfiguration>) => void;
    setWorkflowMessagingTemplateCache: (workflowMessagingTemplateCache: MessagingTemplateCache) => void;
    generateStartChangeRequestButton: (
        setWorkflowChangeRequests: (changeRequests: Array<WorkflowChangeRequest>) => void
    ) => React.ReactNode;
};

export const WorkflowServiceDetailsModal = (props: WorkflowServiceDetailsModalProps) => {
    const {
        initialSelectedService,
        initialRpServiceConfigurations,
        hiddenServiceApiKeys,
        availableLanguages,
        initialWorkflowMessagingTemplateCache,
        initialWorkflowChangeRequests,
        initialSelectedLanguage,
        onModalClose,
        setRpServiceConfigurationsState,
        setWorkflowMessagingTemplateCache,
        generateStartChangeRequestButton
    } = props;

    const [workflowChangeRequests, setWorkflowChangeRequests] = useState(initialWorkflowChangeRequests);
    const [hasFetchedMessagingTemplates, setHasFetchedMessagingTemplates] = useState<{ [rpServiceId: string]: boolean }>({ [initialSelectedService.id]: false });
    const [initialWorkflowMessagingTemplateCacheState, setInitialWorkflowMessagingTemplateCacheState] = useState<MessagingTemplateCache>(initialWorkflowMessagingTemplateCache);
    const [workflowMessagingTemplateCacheState, setWorkflowMessagingTemplateCacheState] = useState<MessagingTemplateCache>(initialWorkflowMessagingTemplateCache);
    const [selectedService, setSelectedService] = useState<FormattedRpServiceConfiguration>(initialSelectedService);
    const [rpServiceConfigurations, setRpServiceConfigurations] = useState<Array<FormattedRpServiceConfiguration>>(initialRpServiceConfigurations);
    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(
        initialSelectedLanguage
    );
    const dispatch = useDispatch();

    const hasUnsubmittedChangeRequest = useMemo(() => {
        if (workflowChangeRequests.length > 0) {
            return workflowChangeRequests[0].changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED;
        }
        return false;
    }, [workflowChangeRequests]);



    const isUIDisabled = useMemo(() => {
        return !selectedService.permissions.canModify || !hasUnsubmittedChangeRequest;
    }, [selectedService.permissions.canModify, hasUnsubmittedChangeRequest]);


    const modalContext = useModalContext();
    const { session } = useSelector(getSessionState);
    const { axnRPServices, messagingTemplates } = useSelector(getCommonReferenceDataState);

    const allowedMessagingTemplates = useMemo(() => {
        if (selectedService.relyingParty) {
            return messagingTemplates.filter(mt => (
                !mt.companyID ||
                (mt.relyingPartyID && mt.relyingPartyID === selectedService.relyingParty.id) ||
                (mt.companyID && !mt.relyingPartyID && mt.companyID === selectedService.relyingParty.companyID)
            ));
        } else {
            return messagingTemplates.filter(mt => !mt.companyID);
        }

    }, [messagingTemplates, selectedService]);

    const {
        availableMessagingTemplateTypes,
        mergedEmailTemplateFields,
        inheritedEmailTemplateFields
    } = useMemo(() => {
        return getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig(selectedService);
    }, [selectedService]);

    const selectedMessagingTemplates = useMemo(() => {
        return getSelectedMessagingTemplates(availableMessagingTemplateTypes, mergedEmailTemplateFields, workflowMessagingTemplateCacheState);
    }, [availableMessagingTemplateTypes, mergedEmailTemplateFields, workflowMessagingTemplateCacheState]);

    const referenceData = useReferenceDataSelector<VerificationServiceDetailsReferenceData>(VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES);

    const resource = resources.modals.workflowServiceDetails;

    const stateIsUnchanged = useMemo(() => {
        for (let i = 0; i < rpServiceConfigurations.length; i++) {
            const serviceConfig = rpServiceConfigurations[i];
            if (hasFetchedMessagingTemplates[serviceConfig.id]) {
                const {
                    availableMessagingTemplateTypes: currentAMTT,
                    mergedEmailTemplateFields: currentMETF
                } = getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig(serviceConfig);

                const currentSelectedMessagingTemplates = getSelectedMessagingTemplates(currentAMTT, currentMETF, workflowMessagingTemplateCacheState);

                const initialServiceConfig = initialRpServiceConfigurations.find(initialConfig => initialConfig.id === serviceConfig.id);
                if (initialServiceConfig) {
                    const {
                        availableMessagingTemplateTypes: initialAMTT,
                        mergedEmailTemplateFields: initialMETF
                    } = getAvailableMessagingTemplateTypesAndEmailTemplateFieldsFromRpServiceConfig(initialServiceConfig);

                    const initialSelectedMessagingTemplates = getSelectedMessagingTemplates(initialAMTT, initialMETF, initialWorkflowMessagingTemplateCacheState);

                    if (!isEqual(currentSelectedMessagingTemplates, initialSelectedMessagingTemplates)) {
                        return false;
                    }
                }
            }
        }

        return isEqual(initialRpServiceConfigurations, rpServiceConfigurations);
    }, [
        initialRpServiceConfigurations,
        rpServiceConfigurations,
        initialWorkflowMessagingTemplateCacheState,
        workflowMessagingTemplateCacheState,
        hasFetchedMessagingTemplates
    ]);


    const onChangeSelectedService = (newOption: SelectOption) => {
        const newSelectedService = rpServiceConfigurations.find(rpsc => rpsc.id === newOption.value);
        setSelectedService(newSelectedService);
    };

    const handleUpdateAxnRpServices = (rpServices: Array<FormattedRpServiceConfiguration>) => {
        const { hasAtLeastOneRpServiceUpdated, updatedRpServices } = getUpdatedAxnRpServices(rpServices, axnRPServices);

        if (hasAtLeastOneRpServiceUpdated) {
            dispatch(setPartialCommonReferenceData({ axnRPServices: updatedRpServices }, CommonReferenceDataPageTypes.WORKFLOW_DETAILS));
        }
    }

    const updateRpServiceConfigurations = (newSelectedService: FormattedRpServiceConfiguration) => {
        const updatedRpServices = rpServiceConfigurations.map(rpsc => {
            if (rpsc.id === newSelectedService.id) {
                return { ...newSelectedService };
            }

            return rpsc
        });

        setRpServiceConfigurations(updatedRpServices);
        handleUpdateAxnRpServices(updatedRpServices);
    };

    const handleSetHasFetchedMessagingTemplates = (templateCache: MessagingTemplateCache) => {
        setHasFetchedMessagingTemplates({
            ...hasFetchedMessagingTemplates,
            [selectedService.id]: true
        });

        setInitialWorkflowMessagingTemplateCacheState({
            ...initialWorkflowMessagingTemplateCacheState,
            ...templateCache
        });
    };

    const handleSetMessagingTemplateCache = (templateCache: MessagingTemplateCache) => {
        setWorkflowMessagingTemplateCacheState({
            ...workflowMessagingTemplateCacheState,
            ...templateCache
        });
    };

    const onUpdateSelectedService = (newSelectedService: FormattedRpServiceConfiguration) => {
        setSelectedService(newSelectedService);
        updateRpServiceConfigurations(newSelectedService);
    };

    const handleCloseModal = () => {
        setRpServiceConfigurationsState(rpServiceConfigurations);
        setWorkflowMessagingTemplateCache(workflowMessagingTemplateCacheState);
        onModalClose();
    };

    const onSubmit = () => {
        onModalClose();
    };

    const handleCancelChanges = () => {
        if (!stateIsUnchanged) {
            const resource = resources.modals.cancelRpServiceChanges;
            const renderModal = (onModalClose: () => void) => {
                return (
                    <ConfirmModal
                        id={"cancel-rpservice-changes"}
                        title={resource.title}
                        confirmMessage={resource.confirmMessage}
                        cancelButtonText={resource.buttons.cancel.label}
                        submitButtonText={resource.buttons.submit.label}
                        onSubmit={onSubmit}
                        onSubmitAsync={null}
                        onModalClose={onModalClose}
                        customProps={null}
                    />
                );
            };

            modalContext.setModalState({
                modalType: ModalTypes.CONFIRM,
                renderFunction: renderModal
            });
        } else {
            onModalClose();
        }
    }

    const serviceOptions: Array<SelectOption> = useMemo(() => {
        return rpServiceConfigurations.map(rpsc => ({
            label: `${rpsc.serviceName} (${rpsc.apiKey})`,
            value: rpsc.id
        })).sort((a, b) => sortStrings(a.label, b.label));
    }, [rpServiceConfigurations]);

    const selectedServiceOption: SelectOption = useMemo(() => {
        return serviceOptions.find(so => so.value === selectedService.id);
    }, [serviceOptions, selectedService]);

    const obligationAxnRpServices = useMemo(() => {
        return excludeServicesByApiKeys(axnRPServices, hiddenServiceApiKeys);
    }, [axnRPServices, hiddenServiceApiKeys]);

    const simpleRPServiceWorkflows = useMemo(() => {
        return hasUnsubmittedChangeRequest ? referenceData.simpleAXNAdminRPServiceWorkflows : referenceData.simpleAXNRPServiceWorkflows;
    }, [hasUnsubmittedChangeRequest, referenceData.simpleAXNRPServiceWorkflows, referenceData.simpleAXNAdminRPServiceWorkflows]);

    const apAssertionStatusMaps: Array<ApAssertionStatusMap> = useMemo(() => {
        return getApAssertionStatusMaps(referenceData.apAssertions, referenceData.axnAssertionStatusMaps, referenceData.assertionStatusDefaults);
    }, []);

    return (
        <DefaultModal
            automationId={`workflow-service-details-modal`}
            ariaLabelledBy={`workflow-service-details-title`}
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={true}
            className={styles.workflowServiceDetailsModal}
        >
            <Modal.Header id={`workflow-service-details-modal-title`} closeButton>
                <Modal.Title>{resource.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={styles.header}>
                    <div className={styles.workflowServiceSelect}>
                        <SelectFormGroup
                            automationId={`workflow-service-select`}
                            controlId={`workflow-service-select`}
                            ariaLabel={resource.inputs.service.label}
                            label={resource.inputs.service.label}
                            disabled={false}
                            isClearable={false}
                            isRequired={false}
                            isSearchable={true}
                            onChange={onChangeSelectedService}
                            options={serviceOptions}
                            selectedOption={selectedServiceOption}
                        />
                    </div>

                    {availableLanguages.length > 0 && (<div>
                        <LanguageSelectorContainer
                            idPrefix="workflow-visualization-service"
                            availableLanguages={availableLanguages}
                            selectedLanguage={selectedLanguage}
                            languageSelectorContainerType={LanguageSelectorContainerTypes.WORKFLOW_MODAL}
                            setSelectedLanguage={setSelectedLanguage}
                        />
                    </div>)}

                </div>

                <hr />
                <VerificationServiceDetailsTabContainer
                    axnRpServices={obligationAxnRpServices}
                    axnCompanies={referenceData.axnCompanies}
                    availablePropertyCategories={referenceData.axnAvailablePropertyCategories}
                    axnAcquiredAttributes={referenceData.axnAcquiredAttributes}
                    requestInProgress={false}
                    rpServiceConfigurationState={selectedService}
                    setRpServiceConfigurationState={onUpdateSelectedService}
                    attributeLabels={referenceData.axnAttributeLabels}
                    assertionAttributes={referenceData.axnAssertionAttributes}
                    axnAttributeTypes={referenceData.axnAttributeTypes}
                    axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                    axnRelyingParties={referenceData.axnRelyingParties}
                    serviceOfferingRefreshRateDefault={referenceData.serviceOfferingRefreshRateDefault}
                    serviceOfferingRefreshRates={referenceData.serviceOfferingRefreshRates}
                    assertionStatusMaps={apAssertionStatusMaps}
                    scoreReferenceData={{
                        assertions: referenceData.axnAssertions,
                        availableAssertionStatuses: referenceData.axnAvailableAssertionStatuses,
                        assertionScoreAssertionStatusValueMaps: referenceData.assertionScoreAssertionStatusValueMaps,
                        availableAssertionScoreValues: referenceData.availableAssertionScoreValues,
                        availableTrustScoreValues: referenceData.availableTrustScoreValues,
                        trustScoreAssertionStatusValueMaps: referenceData.trustScoreAssertionStatusValueMaps
                    }}
                    assertionScoreServiceOfferingIDs={referenceData.assertionScoreServiceOfferings}
                    trustScoreServiceOfferingIDs={referenceData.trustScoreServiceOfferings}
                    scoreAttributeMaps={referenceData.scoreAttributeMaps}
                    serviceAttributeProperties={referenceData.rpServiceAttributeProperties}
                    simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                    simpleAxnWorkflows={referenceData.simpleAXNWorkflows}
                    simpleAxnAdminWorkflows={referenceData.axnAdminWorkflows}
                    axnDocumentationLinks={referenceData.documentationLinks}
                    axnAvailableAssertionStatuses={referenceData.axnAvailableAssertionStatuses}
                    initialRpServiceConfiguration={initialSelectedService}
                    isDisabled={isUIDisabled}
                    availableLanguages={availableLanguages}
                    selectedLanguage={selectedLanguage}
                    hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                    availableMessagingTemplateTypes={availableMessagingTemplateTypes}
                    inheritedEmailTemplateFields={inheritedEmailTemplateFields}
                    mergedEmailTemplateFields={mergedEmailTemplateFields}
                    selectedMessagingTemplates={selectedMessagingTemplates}
                    messagingTemplateCache={workflowMessagingTemplateCacheState}
                    messagingTemplateConfigurations={allowedMessagingTemplates}
                    setMessagingTemplateCache={handleSetMessagingTemplateCache}
                    setAvailableLanguages={()=>{}}
                    setSelectedLanguage={setSelectedLanguage}
                    setHasFetchedMessagingTemplates={handleSetHasFetchedMessagingTemplates}
                    generateHeaderComponent={null}
                    session={session}
                    axnAttributeTypeFieldCustomizations={referenceData.axnAttributeTypeFieldCustomizations}
                />
            </Modal.Body>
            <Modal.Footer>
                {isUIDisabled && (
                    <Button id={`workflow-service-details-close`} variant="secondary" onClick={handleCancelChanges}>{resources.modals.workflowServiceModal.footerButtons.close.label}</Button>
                )}
                {!isUIDisabled && (
                    <>
                        <Button id={`workflow-service-details-cancel`} variant="secondary" onClick={handleCancelChanges}>{resources.modals.workflowServiceModal.footerButtons.cancel.label}</Button>
                        <Button
                            id={`workflow-service-details-confirm-changes`}
                            variant="primary"
                            onClick={handleCloseModal}
                            disabled={stateIsUnchanged}
                        >
                            {resources.modals.workflowServiceModal.footerButtons.confirmAndClose.label}
                        </Button>
                    </>
                )}
                {selectedService.permissions.canModify && !hasUnsubmittedChangeRequest && (
                    generateStartChangeRequestButton(setWorkflowChangeRequests)
                )}
            </Modal.Footer>
        </DefaultModal>
    );
};